import { PROJECT_FILE_KIND_LABELS, type ProjectFileKind, type ProjectFileView } from '@scoutaround/script-breakdown-shared';

import { Icon } from '../Icon.js';
import { MoreMenu, type MenuItem } from '../ui/MoreMenu.js';
import { StatusPill } from '../ui/StatusPill.js';
import { absoluteTime, formatBytes, personLabel, relativeTime } from '../references/format.js';
import { extensionLabel, FILE_KIND_ICON, fileStatus } from './model.js';

/**
 * WHAT A FILE'S KIND LOOKS LIKE: its icon on a tile tinted by kind, with the
 * extension under it on a card. No picture of the file itself — an original is
 * never served (it is held until file checking exists), so nothing is drawn from it.
 */
export function FileKindTile({
  kind,
  filename,
  size,
  testId,
}: {
  kind: ProjectFileKind;
  filename: string;
  size: 'card' | 'row' | 'detail';
  testId?: string;
}) {
  const extension = extensionLabel(filename);
  return (
    <span className="files-kind" data-kind={kind} data-size={size} data-testid={testId} aria-hidden="true">
      <Icon name={FILE_KIND_ICON[kind]} size={size === 'row' ? 18 : size === 'card' ? 30 : 26} strokeWidth={1.5} />
      {size !== 'row' && extension !== '' ? <span className="files-kind-ext">{extension}</span> : null}
    </span>
  );
}

/**
 * ONE FILE IN THE LIST — a row, or a card in the grid. The name is the item's one
 * button, stretched over the whole item, so it opens from anywhere and is still a
 * single focus stop; the ⋯ menu sits above that stretch.
 */
export function FileCard({
  file,
  view,
  selected,
  onOpen,
  menu,
}: {
  file: ProjectFileView;
  view: 'list' | 'grid';
  selected: boolean;
  onOpen: () => void;
  menu: readonly MenuItem[];
}) {
  const status = fileStatus(file);
  const who = personLabel(file.uploadedBy.displayName);

  return (
    <article
      className="files-item"
      data-view={view}
      data-selected={selected ? '1' : undefined}
      data-testid={`files-item-${file.id}`}
    >
      <FileKindTile kind={file.kind} filename={file.originalFilename} size={view === 'grid' ? 'card' : 'row'} testId={`files-item-kind-${file.id}`} />
      <div className="files-item-body">
        <h3 className="files-item-title">
          <button
            type="button"
            className="files-item-open"
            aria-current={selected ? 'true' : undefined}
            data-file-row={file.id}
            data-testid={`files-item-open-${file.id}`}
            onClick={onOpen}
          >
            {file.originalFilename}
          </button>
        </h3>
        <p className="files-item-meta" data-testid={`files-item-meta-${file.id}`}>
          <span className="files-meta-group">
            <span>{PROJECT_FILE_KIND_LABELS[file.kind]}</span>
            <span>{formatBytes(file.byteSize)}</span>
          </span>
          <span className="files-meta-group">
            <span data-testid={`files-item-uploader-${file.id}`}>{who}</span>
            <time dateTime={file.createdAt} title={absoluteTime(file.createdAt)}>
              {relativeTime(file.createdAt)}
            </time>
          </span>
        </p>
      </div>
      <span className="files-item-status">
        <StatusPill state={status.state} label={status.label} size="sm" testId={`files-item-status-${file.id}`} />
      </span>
      <div className="files-item-menu">
        <MoreMenu items={menu} label={`Actions for ${file.originalFilename}`} testId={`files-item-menu-${file.id}`} />
      </div>
    </article>
  );
}

/** Loading placeholders in the shape of the items they stand for. */
export function FileSkeletons({ view, count = 5 }: { view: 'list' | 'grid'; count?: number }) {
  return (
    <div className="files-results" data-view={view} aria-hidden="true" data-testid="files-skeletons">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="files-item files-skeleton" data-view={view}>
          <span className="files-kind" data-size={view === 'grid' ? 'card' : 'row'} />
          <div className="files-item-body">
            <span className="ref-skeleton-line" style={{ width: '55%' }} />
            <span className="ref-skeleton-line" style={{ width: '35%' }} />
          </div>
        </div>
      ))}
    </div>
  );
}
